import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface HolidaysContextType {
  holidays: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchHolidays: (page?: number, perPage?: number) => Promise<void>;
  addHoliday: (holiday: any) => Promise<void>;
  editHoliday: (holiday: any) => Promise<void>;
  deleteHoliday: (id: number | string) => Promise<void>;
}

export const HolidaysContext = createContext<HolidaysContextType | undefined>(
  undefined,
);

interface HolidaysProviderProps {
  children: ReactNode;
}

export const useHolidaysContext = (): HolidaysContextType => {
  const context = useContext(HolidaysContext);
  if (!context) {
    throw new Error("useHolidaysContext must be used within HolidaysProvider");
  }
  return context;
};

const HolidaysProvider: React.FC<HolidaysProviderProps> = ({ children }) => {
  const [holidays, setHolidays] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all holidays with pagination
  const fetchHolidays = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/holidays?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let holidaysData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          holidaysData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          holidaysData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          holidaysData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        holidaysData = responseData.data;
        paginationData = responseData.pagination;
      } else if (Array.isArray(responseData)) {
        holidaysData = responseData;
      }
      
      setHolidays(holidaysData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch holidays:", error);
      setHolidays([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new holiday
  const addHoliday = async (holiday: any): Promise<void> => {
    try {
      const response = await api.post("/holidays", holiday);
      const newHoliday = response.data.data || response.data;
      setHolidays((prev) => [...prev, newHoliday]);
    } catch (error) {
      console.error("Failed to add holiday:", error);
      throw error;
    }
  };

  // Edit an existing holiday
  const editHoliday = async (holiday: any): Promise<void> => {
    try {
      const response = await api.put(`/holidays/${holiday.id}`, holiday);
      const updatedHoliday = response.data.data || response.data;
      setHolidays((prev) =>
        prev.map((item) => (item.id === holiday.id ? updatedHoliday : item))
      );
    } catch (error) {
      console.error("Failed to edit holiday:", error);
      throw error;
    }
  };

  // Delete a holiday
  const deleteHoliday = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/holidays/${id}`);
      setHolidays((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete holiday:", error);
      throw error;
    }
  };

  // REMOVED auto-fetch useEffect - components will call fetchHolidays explicitly with pagination

  return (
    <HolidaysContext.Provider
      value={{
        holidays,
        pagination,
        loading,
        fetchHolidays,
        addHoliday,
        editHoliday,
        deleteHoliday,
      }}
    >
      {children}
    </HolidaysContext.Provider>
  );
};

export default HolidaysProvider;
